import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { RootState } from "../store"; // <-- ensure correct path

export interface Contract {
  id: string;
  start_date: string;
  end_date: string;
  category_id: string;
  category_name: string;
  rate_per_gram: string;
  weight: string;
  billing_cycle: string;
  active: string;
  view_link: string;
  assigned_value?: string;
  used_value?: string;
  remaining_value?: string;
}

interface ContractsState {
  customerId: string | null;
  contracts: Contract[];
  loading: boolean; // generic flag if you still need it
  error: string | null;
  assignedValueSum: string;
  usedValueSum: string;
  remainingValueSum: string;

  // ✅ per-customer dedupe/TTL
  loadingById: Record<string, boolean>;
  loadedById: Record<string, boolean>;
  lastFetchedAtById: Record<string, number>;
}

const initialState: ContractsState = {
  customerId: null,
  contracts: [],
  loading: false,
  error: null,
  assignedValueSum: "0.00",
  usedValueSum: "0.00",
  remainingValueSum: "0.00",

  loadingById: {},
  loadedById: {},
  lastFetchedAtById: {},
};

const api = axios.create({
  baseURL: "https://my.axlpl.com/messenger/services_v7",
  headers: { Accept: "application/json" },
});

type FetchArg = string | { customerId: string; force?: boolean }; // allow force refresh

const getIdFromArg = (arg: FetchArg): { id: string; force: boolean } => {
  if (typeof arg === "string") return { id: arg, force: false };
  return { id: arg.customerId, force: !!arg.force };
};

export const fetchContractsByCustomer = createAsyncThunk<
  {
    customer_id: string;
    contracts: Contract[];
    assigned_value_sum?: string;
    used_value_sum?: string;
    remaining_value_sum?: string;
  },
  FetchArg,
  { state: RootState; rejectValue: string }
>(
  "contracts/fetchByCustomer",
  async (arg, { rejectWithValue }) => {
    try {
      const { id: customerId } = getIdFromArg(arg);

      const fd = new FormData();
      fd.append("customer_id", customerId);

      const res = await api.post("/view_contract", fd);

      const d = res?.data;
      if (d?.status === "success" && Array.isArray(d?.contracts)) {
        return {
          customer_id: String(d.customer_id ?? customerId),
          contracts: d.contracts as Contract[],
          assigned_value_sum: d.assigned_value_sum,
          used_value_sum: d.used_value_sum,
          remaining_value_sum: d.remaining_value_sum,
        };
      }
      return rejectWithValue(d?.message || "Failed to fetch contracts");
    } catch (e: any) {
      console.error("[contracts] request error:", e);
      return rejectWithValue(
        e?.response?.data?.message || e.message || "Request failed",
      );
    }
  },
  {
    // ✅ Prevent duplicate calls for the same customer_id
    condition: (arg, { getState }) => {
      const { id: customerId, force } = getIdFromArg(arg);
      const state = getState() as RootState;
      const s = state.contracts;

      if (!customerId) return false;
      if (force) return true;

      // skip if already loading for this id
      if (s.loadingById[customerId]) return false;

      // skip if already loaded for this id
      if (s.loadedById[customerId]) {
        // Optional TTL (e.g., 120s). Uncomment to enable:
        // const ttlMs = 120_000;
        // const last = s.lastFetchedAtById[customerId] || 0;
        // if (Date.now() - last > ttlMs) return true; // refetch after TTL
        return false;
      }

      return true;
    },
  },
);

const contractsSlice = createSlice({
  name: "contracts",
  initialState,
  reducers: {
    clearContracts(state) {
      state.contracts = [];
      state.customerId = null;
      state.error = null;
      state.loading = false;
      state.assignedValueSum = "0.00";
      state.usedValueSum = "0.00";
      state.remainingValueSum = "0.00";
      // allow refetch for all ids again
      state.loadingById = {};
      state.loadedById = {};
      state.lastFetchedAtById = {};
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchContractsByCustomer.pending, (s, a) => {
      const { id } = getIdFromArg(a.meta.arg);
      s.loading = true; // optional generic flag
      s.error = null;
      s.loadingById[id] = true;
      // Don't clear s.contracts here if you want to keep old data during refresh.
      // If you *must* clear, do it conditionally only when not force-refreshing.
    });

    b.addCase(fetchContractsByCustomer.fulfilled, (s, a) => {
      const id = a.payload.customer_id;
      s.loading = false;
      s.customerId = id;
      s.contracts = a.payload.contracts;
      s.assignedValueSum = a.payload.assigned_value_sum ?? "0.00";
      s.usedValueSum = a.payload.used_value_sum ?? "0.00";
      s.remainingValueSum = a.payload.remaining_value_sum ?? "0.00";

      s.loadingById[id] = false;
      s.loadedById[id] = true;
      s.lastFetchedAtById[id] = Date.now();
    });

    b.addCase(fetchContractsByCustomer.rejected, (s, a) => {
      const { id } = getIdFromArg(a.meta.arg);
      s.loading = false;
      s.error = a.payload ?? "Unable to fetch contracts";
      s.loadingById[id] = false;
      // don't set loadedById on failure
    });
  },
});

export const { clearContracts } = contractsSlice.actions;
export default contractsSlice.reducer;
